import { lazy, Suspense, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { BookingProvider } from '../context/BookingContext';

const BookingModal = lazy(() => import('./booking/BookingModal'));

const MeetOurDoctors = () => {
  const { t, i18n } = useTranslation();
  const [isScrolled, setIsScrolled] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const currentLang = i18n.language || 'en';
  let buttonWidthClass = 'w-[230px] h-[56px] ml-[-174px]';
  let spanWidthClass = 'w-[135px] opacity-100 mr-2.5';

  if (currentLang === 'sn') {
    buttonWidthClass = 'w-[310px] h-[56px] ml-[-254px]';
    spanWidthClass = 'w-[230px] opacity-100 mr-3';
  } else if (currentLang === 'tn') {
    buttonWidthClass = 'w-[320px] h-[56px] ml-[-264px]';
    spanWidthClass = 'w-[240px] opacity-100 mr-3';
  }

  const toggleVisibility = () => {
    if (window.pageYOffset > 300) {
      setIsScrolled(true);
    } else {
      setIsScrolled(false);
    }
  };

  const handleBookDoctor = () => {
    setIsModalOpen(true);
  };

  useEffect(() => {
    window.addEventListener('scroll', toggleVisibility);
    return () => {
      window.removeEventListener('scroll', toggleVisibility);
    };
  }, []);

  useEffect(() => {
    try {
      const url = new URL(window.location.href);
      const params = url.searchParams;

      const hasDirectGatewayMarker = Boolean(
        params.get('MerchantID') ||
        params.get('merchantRID') ||
        params.get('merchantId') ||
        params.get('PaymentGatewayRef') ||
        params.get('paymentGatewayRef') ||
        params.get('trId') ||
        params.get('TrId') ||
        params.get('ExtRefId') ||
        params.get('extRefId') ||
        params.get('txnid')
      );

      if (hasDirectGatewayMarker) {
        const merchantId = params.get('merchantId') || params.get('MerchantID') || params.get('merchantRID');
        let paymentGatewayRef = params.get('paymentGatewayRef') || params.get('PaymentGatewayRef');
        const extRefId = params.get('extRefId') || params.get('ExtRefId');
        let trId = params.get('trId') || params.get('TrId');
        const billId = params.get('billId') || params.get('BillId');

        if (!trId) {
          const txnIdRaw = params.get('txnid') || '';
          const trMatch = String(txnIdRaw).match(/[?&]trId=([^&]+)/i);
          if (trMatch && trMatch[1]) {
            trId = decodeURIComponent(trMatch[1]);
          }
        }
        if (!paymentGatewayRef && trId) {
          paymentGatewayRef = trId;
        }

        if (merchantId) window.localStorage.setItem('medica_pg_merchantId', merchantId);
        if (paymentGatewayRef) window.localStorage.setItem('medica_pg_paymentGatewayRef', paymentGatewayRef);
        if (extRefId) window.localStorage.setItem('medica_pg_extRefId', extRefId);
        if (trId) window.localStorage.setItem('medica_pg_trId', trId);
        if (billId) window.localStorage.setItem('medica_pg_billId', billId);
        window.localStorage.setItem('medica_pg_returnedAt', String(Date.now()));
        window.localStorage.setItem('medica_pg_returnQuery', url.search || '');

        params.delete('merchantId');
        params.delete('MerchantID');
        params.delete('merchantRID');
        params.delete('merchantRid');
        params.delete('paymentGatewayRef');
        params.delete('PaymentGatewayRef');
        params.delete('extRefId');
        params.delete('ExtRefId');
        params.delete('trId');
        params.delete('TrId');
        params.delete('txnid');
        params.delete('billId');
        params.delete('BillId');
        params.delete('status');
        params.delete('Status');

        const nextUrl = `${url.pathname}${params.toString() ? `?${params.toString()}` : ''}${url.hash || ''}`;
        window.history.replaceState({}, document.title, nextUrl);
      }

      const returnedAt = window.localStorage.getItem('medica_pg_returnedAt');
      const resumeRaw = window.localStorage.getItem('medica_pg_resume_v1');
      const consumed = window.localStorage.getItem('medica_pg_resume_consumed');
      if (!returnedAt || !resumeRaw || consumed === '1') return;

      const resume = JSON.parse(resumeRaw);
      const expiresAt = Number(resume?.expiresAt || 0);
      if (!expiresAt || Date.now() > expiresAt) return;

      window.localStorage.setItem('medica_pg_resume_consumed', '1');
      setIsModalOpen(true);
    } catch (e) {
      // ignore
    }
  }, []);

  const shouldShowFull = !isScrolled || isHovered;

  return (
    <>
    <button
      onClick={handleBookDoctor}
      className={`z-40 bg-gradient-to-r from-[#c8102e] to-[#8b0000] text-white rounded-full shadow-xl flex items-center justify-center font-semibold text-base overflow-hidden transition-all duration-200 ease-out hover:scale-[1.03] hover:shadow-[0_20px_40px_rgba(200,16,46,0.4)] active:scale-[0.97] ${shouldShowFull ? buttonWidthClass : 'w-[50px] h-[50px] ml-0'}`}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      aria-label={t('book_doctor_floating_button')}
    >
      <span className={`whitespace-nowrap inline-block overflow-hidden transition-all duration-150 ease-out ${shouldShowFull ? spanWidthClass : 'w-0 opacity-0 mr-0'}`}>
        {t('book_doctor_floating_button')}
      </span>

      {/* Calendar Icon */}
      <svg 
        width="24" 
        height="24" 
        viewBox="0 0 24 24" 
        fill="none" 
        xmlns="http://www.w3.org/2000/svg"
        className="flex-shrink-0"
      >
        <rect x="3" y="6" width="18" height="15" rx="2" stroke="white" strokeWidth="2" fill="none"/>
        <path d="M3 10H21" stroke="white" strokeWidth="2"/>
        <path d="M7 3V6" stroke="white" strokeWidth="2" strokeLinecap="round"/>
        <path d="M17 3V6" stroke="white" strokeWidth="2" strokeLinecap="round"/>
        <circle cx="7" cy="14" r="1" fill="white"/>
        <circle cx="12" cy="14" r="1" fill="white"/>
        <circle cx="17" cy="14" r="1" fill="white"/>
        <circle cx="7" cy="18" r="1" fill="white"/>
        <circle cx="12" cy="18" r="1" fill="white"/>
      </svg>
    </button>
    <BookingProvider>
      <Suspense fallback={null}>
        {isModalOpen ? <BookingModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} /> : null}
      </Suspense>
    </BookingProvider>
    </>
  );
};

export default MeetOurDoctors;